import React, { useMemo } from 'react';
import { legacyConvertCurrency as convertCurrency } from 'utils/general';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { isDesktop } from 'react-device-detect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AmountDueIcon from 'assets/images/calendar/icon-amount-due.svg';
import DateTimeIcon from 'assets/images/calendar/icon-datetime.svg';
import GuestCountIcon from 'assets/images/calendar/icon-guest-count.svg';
import LeadIcon from 'assets/images/calendar/icon-lead.svg';
import LeadAssignmentIcon from 'assets/images/calendar/icon-lead-assignment.svg';
import LocationIcon from 'assets/images/calendar/icon-location.svg';
import ProjectIcon from 'assets/images/calendar/icon-project.svg';
import ProjectServiceTypeIcon from 'assets/images/calendar/icon-project-type.svg';
import { getAddressLinesFromLocation, handleLocationClick } from 'components/Calendar/utils/address';
import { EventDateTimeDisplay } from './EventDateTimeDisplay';

dayjs.extend(utc);
dayjs.extend(timezone);

const BookedLeadEventCard = ({ eventDetails, userDetails }) => {
  const { timeFormat, timeZone } = userDetails || {};
  const {
    allDay = false,
    startStr = '',
    endStr = '',
    extendedProps = {},
  } = eventDetails || {};
  const {
    leadId,
    leadCustomStatus,
    projectLocation: location = {},
    estimatedBudget = null,
    leadOrganizationCurrency,
    organizationId,
    leadEventTz: displayTz,
    contactFirstName,
    contactLastName,
    contactEmail: leadEmail,
    contactPhone: leadPhone,
    leadProjectName = '',
    leadServiceType = '',
    leadAssignments = [],
    venue,
  } = extendedProps;
  const start = dayjs(startStr);
  const end = dayjs(endStr);
  const projectDate = start ? dayjs(start).utc().format('dddd, MMMM D') : null;

  const whenType = allDay ? 'datespan' : 'timespan';
  const sameDay = useMemo(() => {
    // If no end string, assume it's an event that falls on the same day.
    if (!endStr || endStr === '') return true;
    if (whenType === 'timespan') return start.isSame(end, 'day');
    // For all day events, end time is the next day but at 00:00:00, so subtract
    // 1 day to see if it's the same day.
    return end.subtract(1, 'day').isSame(start, 'day');
  }, [whenType]);

  const addressLines = useMemo(
    () => getAddressLinesFromLocation(location),
    [location],
  );
  const hasAddress = addressLines.addressLine1 || addressLines.addressLine2 || addressLines.formattedAddress;

  const leadName = `${contactFirstName} ${contactLastName}`.trim();
  const guestCount = Number(extendedProps?.estimatedGuestCount) || 0;
  const formattedEstimatedBudget = estimatedBudget && leadOrganizationCurrency
    ? convertCurrency(estimatedBudget, leadOrganizationCurrency)
    : '-';
  const leadStatus = leadCustomStatus?.name;
  const showLink = leadId && organizationId;

  return (
    <div className="event-card info-card">
      <div className="info-card__content">
        <div className="info-card__row">
          <div className="info-card__title ap-text">Booked Project</div>
        </div>

        <div className="info-card__row info-card__row--divider">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={DateTimeIcon}
              alt="date-time icon"
            />
          </div>
          <div>
            {projectDate ? (
              <EventDateTimeDisplay
                start={start}
                end={end}
                timeFormat={timeFormat}
                displayTz={displayTz || timeZone} // Fallback to user's timeZone if event timezone is missing
                originalOffset={start.tz(displayTz || timeZone).utcOffset()}
                whenType={whenType}
                sameDay={sameDay}
              />
            ): '-'}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={LeadIcon}
              alt="lead icon"
            />
          </div>
          <div className="info-card__lead">
            <div className="info-card__lead-header">
              <span className="info-card__lead-name">
                {leadName || '-'}&nbsp;
              </span>
              <span className="info-card__lead-status">
                {leadStatus}
              </span>
            </div>
            <div>{leadEmail || '-'}</div>
            <div>{leadPhone || '-'}</div>
          </div>
        </div>

        <div className="info-card__row info-card__row--divider">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={LeadAssignmentIcon}
              alt="assignments icon"
            />
          </div>
          <div>
            {leadAssignments.length > 0
              ? leadAssignments.map(p => p.name).join(', ')
              : '-'
            }
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={ProjectIcon}
              alt="project icon"
            />
          </div>
          <div>{leadProjectName || '-'}</div>
        </div>

        <div className="info-card__row"
          style={{ ... hasAddress && { position: 'relative' } }}
        >
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={LocationIcon}
              alt="location icon"
            />
          </div>
          <div
            onClick={() => handleLocationClick(addressLines, location, isDesktop)}
            style={{ ... hasAddress && { cursor: 'pointer' } }}
          >
            {venue && <div>{venue}</div>}
            {addressLines.addressLine1 && (
              <div className="info-card__formatted-address">
                <div>
                  {addressLines.addressLine1}
                  {addressLines.addressLine2 ? ',' : ''}
                </div>
                {addressLines.addressLine2 && <div>{addressLines.addressLine2}</div>}
              </div>
            )}
            {!addressLines.addressLine1 && location?.formattedAddress ? (
              <div className="info-card__formatted-address">
                {location.formattedAddress}
              </div>
            ) : null}
            {!venue && !addressLines.addressLine1 && !location?.formattedAddress && '-'}
            {(addressLines.addressLine1 || addressLines.formattedAddress ) && (
              <FontAwesomeIcon
                icon={['fa', 'angle-right']}
                className="click-indicator"
              />
            )}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={ProjectServiceTypeIcon}
              alt="service type icon"
            />
          </div>
          <div>{leadServiceType || '-'}</div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={GuestCountIcon}
              alt="guest count icon"
            />
          </div>
          <div>{guestCount > 0 ? `${guestCount} Guest${guestCount === 1 ? '' : 's'}`: '-'}</div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={AmountDueIcon}
              alt="currency icon"
            />
          </div>
          <div>{formattedEstimatedBudget || '-'}</div>
        </div>
      </div>

      {showLink && (
        <a
          className="button"
          href={`/app/pro/${organizationId}/leads/lead/${leadId}?return_to=calendar`}
        >
          View Lead Record
        </a>
      )}
    </div>
  );
};

export default BookedLeadEventCard;
